import { formatPriceWithCurrency } from '@toggle/helpers/src/utils/currency/currency';
import { ParsedUrlQuery } from 'querystring';

import {
  AssetDetails,
  Earning,
  EarningState,
  Expectation,
} from '@/api/asset/asset-schema';

export const EXPECTATION_KEY = 'earnings';

export const formatEarning = (earning?: Earning, currency?: string) => {
  const value = earning?.surprise || 0;

  return formatPriceWithCurrency(value, currency);
};

export const formatNumberWithUnit = (
  num: number | undefined,
  locale?: string | undefined
) => {
  if (num === undefined) {
    return '';
  }

  const abs = Math.abs(num);

  if (abs === 0) {
    return Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  if (abs / 1e12 >= 1) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  if (abs / 1e9 >= 100) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  if (abs / 1e9 >= 10) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(num);
  }

  if (abs / 1e9 >= 1) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  if (abs / 1e6 >= 100) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 0,
    }).format(num);
  }

  if (abs / 1e6 >= 10) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(num);
  }

  if (abs / 1e6 >= 1) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  if (abs / 1e3 >= 100) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 0,
    }).format(num);
  }

  if (abs / 1e3 >= 10) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(num);
  }

  if (abs / 1e3 >= 1) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  if (abs >= 100) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  if (abs >= 10) {
    return Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(num);
  }

  return Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatPercentageNumber = (num: number) => num.toFixed(2) + '%';

export const checkEarningEstimate = (earning?: Earning) =>
  earning?.estimate !== undefined;

export const getEarningState = (earning?: Earning) => {
  if (!earning || typeof earning.surprise === 'undefined') {
    return undefined;
  }

  if (earning.surprise === 0) {
    return EarningState.Meet;
  } else if (earning.surprise > 0) {
    return EarningState.Beat;
  } else {
    return EarningState.Miss;
  }
};

export const getInitialExpectation = (
  query: ParsedUrlQuery,
  assetDetails: AssetDetails
): Expectation => {
  const { value, estimate } = assetDetails.eps;
  if (assetDetails.reported && value && estimate) {
    return value >= estimate ? Expectation.Beat : Expectation.Miss;
  }

  return (query[EXPECTATION_KEY] as Expectation) || Expectation.Beat;
};

export const getUserLocale = (
  acceptLanguage: string | undefined,
  defaultLocale: string
) => {
  if (!acceptLanguage) {
    return defaultLocale;
  }

  try {
    const maybeUserLocale = acceptLanguage.split(';')[0].split(',')[0];

    if ((0).toLocaleString(maybeUserLocale)) {
      return maybeUserLocale;
    }

    throw new Error('wrong locale');
  } catch (error) {
    return defaultLocale;
  }
};
